document.addEventListener('DOMContentLoaded', () => {
  class ProductCardColorSwatch extends HTMLElement {
    constructor() {
      super();
      this.productCard = this.closest('.js-product-card-wrapper');

      if (!this.productCard) {
        console.warn('ProductCardColorSwatch: parent wrapper not found');
        return;
      }

      this.constants = {
        triggersClass: '.js-product-card-color-swatch--trigger',
        triggersHolderClass: '.js-product-card-color-swatch__element',
        activeClass: 'active'
      };
      this.currentTrigger = null;

      this.elements = {
        productImages: this.productCard?.querySelectorAll('.js-card-media img'),
        triggerHolderList: this.productCard?.querySelectorAll(this.constants.triggersHolderClass),
        inputFormVariantId: this.productCard?.querySelector('form[action="/cart/add"] input.product-variant-id'),
        priceContainer: this.productCard?.querySelector('.js-card-information__price-holder')
      };
    }

    connectedCallback() {
      this.bindEvents();
    }

    /**
     * Extracts product data from the trigger element's dataset attributes.
     * @param {HTMLElement} trigger - The HTML element containing product data attributes
     * @returns {Object} Product data object
     * @returns {string} imageFirstUrl - First product image URL from data-product-image-first attribute
     * @returns {string} imageSecondUrl - Second product image URL from data-product-image-second attribute
     * @returns {string} url - Product URL from data-product-url attribute
     * @returns {string} variantId - Product variant ID from data-product-variant-id attribute
     */
    getProductData(trigger) {
      return {
        imageFirstUrl: trigger.dataset.productImageFirst,
        imageSecondUrl: trigger.dataset.productImageSecond,
        url: trigger.dataset.productUrl,
        variantId: trigger.dataset.productVariantId
      };
    }

    changeImage({ imageFirstUrl, imageSecondUrl }) {
      const { productImages } = this.elements;
      if (!productImages?.length) return;

      if (imageFirstUrl && productImages[0]) {
        productImages[0].setAttribute('srcset', imageFirstUrl);
        productImages[0].setAttribute('src', imageFirstUrl);
      }

      if (imageSecondUrl && productImages[1]) {
        productImages[1].setAttribute('srcset', imageSecondUrl);
        productImages[1].setAttribute('src', imageSecondUrl);
      }
    }

    changeVariantId({ variantId }) {
      const { inputFormVariantId } = this.elements;
      if (!inputFormVariantId) return;
      inputFormVariantId.value = variantId;
    }

    changePrice() {
      const { priceContainer } = this.elements;
      const { triggersHolderClass } = this.constants;
      const priceTemplate = this.currentTrigger
        ?.closest(triggersHolderClass)
        ?.querySelector('.js-product-card-color-swatch__price-template');

      if (!priceContainer || !priceTemplate) return;

      const templateContent = priceTemplate.content.cloneNode(true);
      priceContainer.replaceChildren(templateContent);
    }

    changeUrl({ url }) {
      const linkList = this.productCard?.querySelectorAll('a[href*="/products/"]');
      if (!linkList?.length) return;

      linkList.forEach(link => link.setAttribute('href', url));
    }

    setActiveClass() {
      const { activeClass, triggersHolderClass } = this.constants;
      const currentTriggerHolder = this.currentTrigger?.closest(triggersHolderClass);

      if (!currentTriggerHolder) return;

      this.elements.triggerHolderList.forEach(item => item.classList.remove(activeClass));
      currentTriggerHolder.classList.add(activeClass);
    }

    updateInfoInCard(productData) {
      this.changeImage(productData);
      this.changeVariantId(productData);
      this.changePrice();
      this.changeUrl(productData);
    }

    setCurrentTrigger(trigger) {
      this.currentTrigger = trigger;
    }

    bindEvents() {
      const { triggersClass } = this.constants;

      this.addEventListener('click', (event) => {
        const trigger = event.target.closest(triggersClass);

        if (!trigger) return;

        event.preventDefault();
        this.setCurrentTrigger(trigger);

        const productData = this.getProductData(trigger);
        this.updateInfoInCard(productData);
        this.setActiveClass();
      });
    }
  }

  customElements.define('product-card-color-swatch', ProductCardColorSwatch);

  /* How to use:

  Include:
    {{ 'component-accordion-item.out.css' | asset_url | stylesheet_tag }}
    <script src="{{ 'accordion-item.out.js' | asset_url }}" defer></script>

  Structure:
    <accordion-item class="footer-accordion-item accordion-item" aria-expanded="true">
      <button class="accordion-item-trigger js-accordion-item-trigger"
          tabindex="0"
          aria-controls="footer-menu-list"
      >
        Shop
      </button>

      <div class="footer-accordion-item__content accordion-item__content-wrapper">
        <div class="accordion-item__content-holder">
          <div class="accordion-item__content">
            ... content
          </div>
        </div>
      </div>
    </accordion-item>


  Group behavior (optional):
    Add data-accordion-group attribute to group accordion items together.
    Only one item in the group can be open at a time.

    <accordion-item data-accordion-group="footer" aria-expanded="false">
      <!-- First accordion item -->
    </accordion-item>

    <accordion-item data-accordion-group="footer" aria-expanded="false">
      <!-- Second accordion item -->
    </accordion-item>

  Features:
    - Click and keyboard navigation (Enter/Space) support
    - ARIA accessibility attributes
    - Optional group behavior for mutual exclusion
    - Smooth state transitions
  * */

  class AccordionItem extends HTMLElement {
    constructor() {
      super();
      this.status = {
        open: 'true',
        close: 'false'
      };

      this.constants = {
        openAttribute: 'aria-expanded',
        groupItemsAttribute: 'data-accordion-group'
      };
      this.elements = {
        trigger: this.querySelector('.js-accordion-item-trigger')
      };

    }

    /**
     * @returns {'true'|'false'}
     */
    isOpen() {
      const { openAttribute } = this.constants;

      return this.getAttribute(openAttribute) || 'false';
    }

    open() {
      const { openAttribute } = this.constants;
      const { open } = this.status;

      this.closeOthersInGroup();
      this.setAttribute(openAttribute, open);
      return open;
    }

    close() {
      const { openAttribute } = this.constants;
      const { close } = this.status;

      this.setAttribute(openAttribute, close);
      return close;
    }

    closeOthersInGroup() {
      const { groupItemsAttribute } = this.constants;
      if (!this.hasAttribute(groupItemsAttribute)) return;

      const groupName = this.getAttribute(groupItemsAttribute);
      if (!groupName) return;

      const groupItems = document.querySelectorAll(`accordion-item[${groupItemsAttribute}="${groupName}"]`);
      groupItems.forEach(item => {
        if (item !== this && item.isOpen() === this.status.open) {
          item.close();
        }
      });
    }

    toggleState() {
      const { open, close } = this.status;
      const currentStatus = this.isOpen();

      if (currentStatus === open) {
        this.close();
        return;
      }

      if (currentStatus === close) {
        this.open();
        return;
      }
    }

    connectedCallback() {
      const { trigger } = this.elements;

      trigger?.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggleState();
      });


      trigger?.addEventListener('keydown', (event) => {
        if (event.code === 'Enter' || event.code === 'Space') {
          event.preventDefault();
          this.toggleState();
        }
      });
    }
  }

  customElements.define('accordion-item', AccordionItem);
});